/**
 * AI Recruit - Jobs Page
 * Job posting management with AI-powered analysis and matching
 */

import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useAuth } from '../../../hooks/useAuth'
import { usePermission } from '@/hooks/usePermission'
import { formatToCompactDateTime } from '../../../utils/dateUtils'
import { exportJobPostings } from '../../../utils/exportToExcel'
import ConfirmationModal from '../../../components/common/ConfirmationModal'
import { AssignedRecruitersDisplay } from '../../../components/jobs/AssignedRecruitersDisplay'
import SourceSelector from '../../../components/common/SourceSelector'
import AssigneeSelector from '../../../components/jobs/AssigneeSelector'
// import RequirementsAgentWidget from '@/components/requirements-agent/RequirementsAgentWidget'
import type { OrganizationUser } from '../../../services/assignmentApi'

import {
  PlusIcon,
  BriefcaseIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid'

import { jobsApi, matchingApi, type JobAssignment } from '../../../services/api'
import { fetchSources } from '../../../services/sourceApi'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import { downloadJobAsWord } from '../../../utils/downloadJobAsWord'
import SearchableClientDropdown from '../../../components/jobs/SearchableClientDropdown'
import JobRowActionsMenu from '../../../components/jobs/JobRowActionsMenu'
import JobInfoTooltip from '../../../components/jobs/JobInfoTooltip'

interface Job {
  id: string
  job_id?: string
  title: string
  department?: string
  client?: string
  source_id?: string
  source_name?: string
  source_is_active?: boolean
  location?: string
  employment_type: string
  remote_option: string
  description: string
  responsibilities?: string
  requirements?: string
  benefits?: string
  salary_min?: number
  salary_max?: number
  salary_currency: string
  salary_period?: string
  application_deadline?: string
  min_years_experience?: number
  max_years_experience?: number
  status: string
  processing_status?: string
  created_at: string
  updated_at: string
  created_by_name?: string
  updated_by_name?: string
  ai_analysis?: any
  processing_confidence?: number
  assigned_recruiters?: JobAssignment[]
  pipeline_stats?: PipelineStats
}

interface PipelineStats {
  total: number
  submitted: number
  shortlisted: number
  interview: number
  selected: number
  rejected: number
}

interface JobAnalysis {
  id: string
  title: string
  status: string
  analysis?: any
  confidence_score?: number
  processing_time?: number
  error_message?: string
}

interface JobFormData {
  title: string
  department?: string
  client: string
  source_id?: string
  location?: string
  employment_type: string
  remote_option: string
  description: string
  responsibilities?: string
  requirements?: string
  benefits?: string
  salary_min?: number
  salary_max?: number
  salary_currency: string
  salary_period: string
  application_deadline?: string
  min_years_experience?: number
  max_years_experience?: number
  assigned_user_ids?: string[]
  // Carried through from upload-jd's response when this job is being created
  // from an uploaded file (see backend JobFormData's docstring on these same
  // four fields) -- not a visible form input, just threaded through so job
  // creation persists the already-uploaded blob reference. undefined for
  // Enter Details and Paste JD, same as every pre-existing job.
  original_filename?: string
  content_type?: string
  azure_blob_name?: string
  azure_container?: string
}

// Jobs-list polling: interval between checks, and a hard cap on how many checks
// a single query instance will run before giving up — so a job stuck in
// "processing" forever (crashed worker, a task that never completes) can't poll
// indefinitely. Resets naturally whenever the query key changes (new filters,
// search, or page — each gets its own cache entry and its own fresh counter).
const JOBS_POLL_INTERVAL_MS = 5000
const JOBS_POLL_MAX_ATTEMPTS = 120 // ~10 minutes at 5s each

const JobsPage: React.FC = () => {

  // URL-based state: all filter/pagination params live in the URL so that
  // navigating Jobs → Job Details → Back to Jobs restores the exact list state.
  const [searchParams, setSearchParams] = useSearchParams()

  const searchTerm   = searchParams.get('search')     || ''
  const statusFilter = searchParams.get('status')     || ''
  const clientFilter = searchParams.get('client')     || ''
  const sourceFilter = searchParams.get('source')     || ''
  const sortBy       = searchParams.get('sort_by')    || 'created_at'
  const sortOrder    = (searchParams.get('sort_order') || 'desc') as 'asc' | 'desc'
  const currentPage  = parseInt(searchParams.get('page')      || '1',  10)
  const pageSize     = parseInt(searchParams.get('page_size') || '10', 10)

  // searchInput is local-only: the unsubmitted text in the search box.
  // Initialised from URL so the input is pre-filled when navigating back.
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showJDAnalysisModal, setShowJDAnalysisModal] = useState(false)
  const [editingJob, setEditingJob] = useState<Job | null>(null)
  const [extractedJobData, setExtractedJobData] = useState<JobFormData | null>(null)
  const [showLoadingPopup, setShowLoadingPopup] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showCreateDropdown, setShowCreateDropdown] = useState(false)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showFileUploadModal, setShowFileUploadModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const exportDropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [jobIdToDelete, setJobIdToDelete] = useState<string | null>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCreateDropdown(false)
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false)
      }
    }

    if (showCreateDropdown || showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCreateDropdown, showExportDropdown])

  // status (and all other filters) are now read directly from searchParams above.

  // Helper function to shorten Job ID
  const shortenJobId = (jobId: string): string => {
    if (!jobId) return ''
    return jobId.substring(0, 8)
  }

  const truncateJobTitle = (title: string, maxLength: number = 30): string => {
    if (title.length <= maxLength) return title
    return `${title.slice(0, maxLength)}...`
  }

  // Check user roles. Split 2026-08-14 (§10.1): `isAdmin` previously gated
  // both the complexity/confidence score displays (no backend enforcement
  // — stays a plain hierarchy check) AND the Reprocess/"Reanalyze" button
  // (maps to jobs.reprocess, senior_recruiter+ — a deliberate widening,
  // the backend has always allowed this tier). `canEditAssignments`
  // previously gated both the Edit-job button (jobs.edit) and the
  // Activate/Close status toggle (jobs.changeStatus) — two different
  // capabilities that only coincided because they share a tier today.
  const { can } = usePermission()
  const canReprocessJob = can('jobs.reprocess')
  const canEditJob = can('jobs.edit')
  const canChangeJobStatus = can('jobs.changeStatus')
  // Gate mismatch fix (Priority 4 audit) — the "Create Job" button rendered
  // unconditionally even though the backend has always gated job creation
  // at recruiter-tier (gated("jobs.create", ...), jobs.py). Frontend-only
  // change; the backend enforcement this closes the gap with is unchanged.
  const canCreateJob = can('jobs.create')
  // Added 2026-08-25 (permission-catalog round 2, §26) — Download JD had no
  // gate at all (purely client-side, no backend route). Default-open to
  // all roles, matching today's actual unrestricted behavior exactly.
  const canDownloadJD = can('jobs.downloadJD')

  // ── Search helpers ──────────────────────────────────────────────────────────

  const handleSearch = () => {
    const p = new URLSearchParams(searchParams)
    if (searchInput.trim()) {
      p.set('search', searchInput.trim())
    } else {
      p.delete('search')
    }
    p.set('page', '1')
    setSearchParams(p)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    const p = new URLSearchParams(searchParams)
    p.delete('search')
    p.set('page', '1')
    setSearchParams(p)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  // Debounced auto-search: after the user stops typing for 400ms, push the
  // trimmed search term into the URL (same effect as the old Search button).
  // Enter still searches immediately via handleKeyPress above. Uses the
  // functional setSearchParams form so a filter changed elsewhere during the
  // debounce window isn't clobbered by a stale searchParams snapshot.
  useEffect(() => {
    const trimmed = searchInput.trim()
    if (trimmed === searchTerm) return

    const timeoutId = setTimeout(() => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        if (trimmed) {
          p.set('search', trimmed)
        } else {
          p.delete('search')
        }
        p.set('page', '1')
        return p
      })
    }, 400)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // Clear filters only (keeps search term)
  const handleClearFilters = () => {
    const p = new URLSearchParams(searchParams)
    p.delete('status')
    p.delete('client')
    p.delete('source')
    p.delete('sort_by')
    p.delete('sort_order')
    p.set('page', '1')
    setSearchParams(p)
  }

  // Clear a single filter param, leaving the others untouched
  const clearFilterParam = (key: 'status' | 'client' | 'source') => {
    const p = new URLSearchParams(searchParams)
    p.delete(key)
    p.set('page', '1')
    setSearchParams(p)
  }

  const clearSort = () => {
    const p = new URLSearchParams(searchParams)
    p.delete('sort_by')
    p.delete('sort_order')
    p.set('page', '1')
    setSearchParams(p)
  }

  // Count active filters
  // Count sort as active if it's different from default (created_at, desc)
  const isSortActive = sortBy !== 'created_at' || sortOrder !== 'desc'
  const activeFilterCount = [statusFilter, clientFilter, sourceFilter, isSortActive].filter(Boolean).length

  const renderFilterChip = (label: string, value: string, onClear: () => void) => (
    <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
      <span className="font-semibold">{label}:</span> {value}
      <button
        type="button"
        onClick={onClear}
        className="ml-0.5 p-0.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-100"
        aria-label={`Remove ${label} filter`}
      >
        <XMarkIcon className="w-3 h-3" />
      </button>
    </span>
  )

  // Applications column pill — links to the job's Matching tab when the
  // stage has at least one candidate, otherwise renders as a plain,
  // non-clickable pill.
  const renderPipelinePill = (label: string, count: number, jobId: string, colorClasses: string) => {
    const pill = (
      <span className={`inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses}`}>
        {label} <span className="font-semibold">{count}</span>
      </span>
    )
    return count > 0 ? (
      <Link to={`/jobs/${jobId}?tab=matching`} className="justify-self-start hover:opacity-80 transition-opacity">
        {pill}
      </Link>
    ) : (
      <span className="justify-self-start">{pill}</span>
    )
  }

  // Download job as Word document
  const handleExportReport = async (format: 'xlsx' | 'csv') => {
    setIsExporting(true)
    setShowExportDropdown(false)
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export, please wait...`)
    try {
      await exportJobPostings(format, {
        status: statusFilter || undefined,
        client: clientFilter || undefined,
        search: searchTerm || undefined,
        source_id: sourceFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      })
      toast.success(`Job postings exported as ${format.toUpperCase()} successfully!`, { id: toastId })
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export job postings. Please try again.', { id: toastId })
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownloadJob = async (job: Job) => {
    try {
      await downloadJobAsWord({
        title: job.title,
        department: job.department,
        location: job.location,
        employment_type: job.employment_type,
        remote_option: job.remote_option,
        description: job.description,
        responsibilities: job.responsibilities,
        requirements: job.requirements,
        benefits: job.benefits,
        salary_min: job.salary_min,
        salary_max: job.salary_max,
        salary_currency: job.salary_currency,
        salary_period: job.salary_period || 'annual'
      })
      toast.success('Job description downloaded successfully!')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download job description')
    }
  }

  // Helper function to refresh jobs list
  const refreshJobsList = async () => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshing) {
      console.log('🔄 REFRESH ALREADY IN PROGRESS - Skipping duplicate refresh call')
      return true
    }
    
    console.log('🔄 REFRESH CALLED - Refreshing jobs list...')
    console.log('🔄 Current filters:', { searchTerm, statusFilter, sortBy, sortOrder })
    console.log('🔄 Refresh call stack:', new Error().stack)
    
    setIsRefreshing(true)
    
    try {
      // Invalidate all job-related queries with a more specific pattern
      // This will match ['jobs', ...] and all its variations
      await queryClient.invalidateQueries({ 
        queryKey: ['jobs'],
        exact: false // This will match all queries that start with ['jobs']
      })
      console.log('✅ Jobs list refresh completed successfully')
      return true
    } catch (error) {
      console.error('❌ Error refreshing jobs list:', error)
      return false
    } finally {
      // Add a small delay before allowing another refresh
      setTimeout(() => {
        setIsRefreshing(false)
      }, 100)
    }
  }

  // Specific refresh function for job creation (with enhanced logging)
  const refreshJobsListAfterCreation = async () => {
    console.log('🎯 JOB CREATION REFRESH - Starting refresh after job creation...')
    return await refreshJobsList()
  }

  // Memoize query configuration to prevent unnecessary re-renders
  const queryConfig = useMemo(() => {
    return {
      queryKey: ['jobs', searchTerm, statusFilter, clientFilter, sourceFilter, sortBy, sortOrder, currentPage, pageSize],
      queryFn: () => {
        console.log('🔄 JOBS API CALL - Fetching jobs with filters:', { searchTerm, statusFilter, clientFilter, sourceFilter, sortBy, sortOrder, currentPage, pageSize })
        return jobsApi.list({
          search: searchTerm || undefined,
          status: statusFilter || undefined,
          client: clientFilter || undefined,
          source_id: sourceFilter || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          page: currentPage,
          page_size: pageSize
        })
      },
      // Time-based polling via React Query itself, not a useEffect keyed on the
      // query result. React Query applies structural sharing — a refetch that
      // comes back identical to what's cached is handed the SAME object
      // reference, so a useEffect gated on that reference would never re-fire
      // while a job sits at "processing" (exactly when we need it to keep
      // polling). refetchInterval doesn't have this problem: React Query
      // re-evaluates it after every fetch attempt regardless of whether the
      // data actually changed.
      //
      // Negative-scenario handling:
      // - query.state.status === 'error': React Query has already exhausted
      //   its own retries for this fetch; stop rather than hammering a failing
      //   endpoint in a loop.
      // - dataUpdateCount cap: bounds the loop even if a job is legitimately
      //   stuck at processing/pending forever (stuck task, crashed worker).
      // - malformed/missing page data: treated as inactive (no poll), not an error.
      refetchInterval: (query: any) => {
        if (query.state.status === 'error') return false
        if (query.state.dataUpdateCount > JOBS_POLL_MAX_ATTEMPTS) return false
        const jobs = query.state.data?.data?.jobs
        if (!Array.isArray(jobs)) return false
        const isActive = jobs.some(
          (job: any) => job.processing_status === 'processing' || job.processing_status === 'pending'
        )
        return isActive ? JOBS_POLL_INTERVAL_MS : false
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
    }
  }, [searchTerm, statusFilter, clientFilter, sourceFilter, sortBy, sortOrder, currentPage, pageSize])

  // Fetch jobs
  const { data: jobsData, isLoading, error, dataUpdatedAt: jobsDataUpdatedAt } = useQuery(queryConfig)

  // Fetch job sources for filter dropdown
  const { data: jobSources = [] } = useQuery({
    queryKey: ['job-sources'],
    queryFn: () => fetchSources('job', false) // Only active sources
  })

  // Server returns pre-sorted results; just unwrap the page data.
  // dataUpdatedAt is in the dependency list (unused in the body) purely so this
  // recomputes on every poll tick — see the refetchInterval comment above for why
  // `jobsData` alone isn't a reliable signal that a refetch actually happened.
  const paginatedJobs = React.useMemo(() => {
    return jobsData?.data?.jobs ?? []
  }, [jobsData, jobsDataUpdatedAt])

  // Get pagination info from server response
  const totalJobs = jobsData?.data?.total || 0
  const totalPages = Math.ceil(totalJobs / pageSize)
  const hasNext = jobsData?.data?.has_next ?? false
  
  // Fetch all unique clients across all jobs (not limited to current page)
  const { data: uniqueClients = [] } = useQuery({
    queryKey: ['job-clients'],
    queryFn: () => jobsApi.listClients().then((res) => res.data as string[]),
    staleTime: 5 * 60 * 1000,
  })
  
  
  
  // Debug query state changes
  React.useEffect(() => {
    if (jobsData) {
      console.log('✅ QUERY SUCCESS - Data received:', jobsData)
    }
  }, [jobsData])
  
  React.useEffect(() => {
    if (error) {
      console.log('❌ QUERY ERROR - Error received:', error)
    }
  }, [error])

  // Create job mutation
  const createJobMutation = useMutation({
    mutationFn: async (jobData: JobFormData) => {
      console.log('Creating job with data:', jobData)
      
      // Close the form first, then show loading popup
      setShowCreateModal(false)
      setShowLoadingPopup(true)
      setLoadingMessage('Creating job posting...')
      
      try {
        const response = await jobsApi.create(jobData)
        console.log('Raw API response:', response)
        console.log('Response status:', response.status)
        console.log('Response headers:', response.headers)
        return response
      } catch (error) {
        console.log('API call failed:', error)
        throw error
      }
    },
    onSuccess: async (response) => {
      console.log('🎉 CREATE JOB MUTATION SUCCESS - Response received')
      console.log('Job creation success response:', response)
      console.log('Response status:', response.status)
      console.log('Response data:', response.data)
      
      setLoadingMessage('Job created successfully! Processing response...')
      console.log('Waiting for API interceptor to complete...')
      
      // Wait a moment for API interceptor to complete processing
      await new Promise(resolve => setTimeout(resolve, 200))
      
      console.log('API interceptor processing complete, waiting for backend to complete job creation...')
      setLoadingMessage('Job created successfully! Waiting for backend to complete...')
      
      // Wait for backend to complete job creation and processing
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      setLoadingMessage('Job created successfully! Refreshing list...')
      
      // Check if the response indicates success or processing
      // Both v1 and v2 return status: "processing" when job is queued for background processing
      if (response.data && (response.data.status === 'success' || response.data.status === 'processing')) {
        // Form is already closed, just refresh the jobs list
        // Refresh the jobs list and wait for completion
        console.log('🔄 CREATE JOB SUCCESS - Starting jobs list refresh...')
        const refreshSuccess = await refreshJobsListAfterCreation()
        console.log('✅ CREATE JOB SUCCESS - Jobs list refresh completed, success:', refreshSuccess)
        
        // Show success message and close loading popup
        setTimeout(() => {
          console.log('Closing loading popup...')
          setShowLoadingPopup(false)
          toast.success('Job posting created successfully!')
        }, refreshSuccess ? 300 : 800) // Shorter delay if refresh was successful
      } else {
        // Handle case where job creation failed
        const errorMsg = response.data?.error_message || 'Unknown error occurred'
        
        // Form is already closed, wait for backend to complete job creation
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Refresh the jobs list and wait for completion
        const refreshSuccess = await refreshJobsListAfterCreation()
        
        // Show error message and close loading popup
        setTimeout(() => {
          setShowLoadingPopup(false)
          toast.error(`Job creation failed: ${errorMsg}`)
        }, refreshSuccess ? 300 : 800) // Shorter delay if refresh was successful
      }
    },
    onError: async (error: any) => {
      console.log('Job creation error:', error)
      console.log('Error response:', error.response)
      console.log('Error response data:', error.response?.data)
      console.log('Error message:', error.message)
      console.log('Error code:', error.code)
      
      // Check if it's actually a network error (no response received)
      if (error.code === 'ERR_NETWORK' || error.code === 'NETWORK_ERROR' || !error.response) {
        console.log('Network error detected, but checking if job was actually created...')
        
        // Close the form first, then show loading popup
        setShowCreateModal(false)
        setShowLoadingPopup(true)
        setLoadingMessage('Processing...')
        
        // Wait a moment for any pending API processing
        await new Promise(resolve => setTimeout(resolve, 300))
        
        setLoadingMessage('Waiting for backend to complete...')
        
        // Wait for backend to complete job creation (even with network error)
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        setLoadingMessage('Refreshing list...')
        
        // Even if there's a network error, the job might have been created
        // Let's refresh the jobs list to see if it appears
        const refreshSuccess = await refreshJobsListAfterCreation()
        
        // Show success message and close loading popup
        setTimeout(() => {
          setShowLoadingPopup(false)
          toast.success('Job created successfully!')
        }, refreshSuccess ? 800 : 1200) // Longer delay if refresh failed
      } else if (error.response?.status >= 400) {
        // Only show error for 4xx and 5xx status codes
        const message = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      error.response?.data?.error ||
                      error.message || 
                      'Failed to create job'
        
        setShowLoadingPopup(false)
        toast.error(message)
      } else {
        // For 2xx status codes, this shouldn't reach onError
        // But if it does, treat as success
        console.log('Unexpected success response in error handler, treating as success')
        
        // Close the form first, then show loading popup
        setShowCreateModal(false)
        setShowLoadingPopup(true)
        setLoadingMessage('Job created successfully! Waiting for backend...')
        
        // Wait for backend to complete job creation
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        setLoadingMessage('Job created successfully! Refreshing list...')
        
        const refreshSuccess = await refreshJobsListAfterCreation()
        
        setTimeout(() => {
          setShowLoadingPopup(false)
          toast.success('Job posting created successfully!')
        }, refreshSuccess ? 500 : 1000) // Shorter delay if refresh was successful
      }
    }
  })

  // Update job mutation
  const updateJobMutation = useMutation({
    mutationFn: async ({ id, data, needsReprocessing }: { id: string, data: Partial<JobFormData>, needsReprocessing: boolean }) => {
      // First update the job
      const updateResponse = await jobsApi.update(id, data)

      // Only reprocess if content fields changed (description, responsibilities, requirements, benefits)
      if (needsReprocessing) {
        await jobsApi.reprocess(id)
      }

      // matches_invalidated (backend) is set when a matching-relevant field
      // (description/responsibilities/requirements/min-max experience)
      // changed — existing matches are stale but were never deleted, so
      // refresh them automatically instead of leaving it to a manual
      // "Apply Selected" click. Same behavior as JobDetailPage's edit modal
      // (this is a second, separate place a job can be edited from).
      return { matchesInvalidated: Boolean(updateResponse?.data?.matches_invalidated), jobId: id }
    },
    onMutate: ({ needsReprocessing }) => {
      // Close the modal immediately when update starts
      setEditingJob(null)
      setShowCreateModal(false)

      // Show loading popup
      setShowLoadingPopup(true)
      setLoadingMessage(needsReprocessing ? 'Updating job and starting reprocessing...' : 'Updating job...')
    },
    onSuccess: async ({ matchesInvalidated, jobId }, { needsReprocessing }) => {
      console.log('🔄 UPDATE JOB SUCCESS', needsReprocessing ? '- Starting reprocessing...' : '- No reprocessing needed')

      if (needsReprocessing) {
        toast.success('Job posting updated successfully! Starting reprocessing...')
        setLoadingMessage('Reprocessing job with updated data...')

        // Wait a moment for backend processing
        await new Promise(resolve => setTimeout(resolve, 2000))

        setLoadingMessage('Job reprocessing started! Refreshing list...')
      } else {
        toast.success('Job posting updated successfully!')
        setLoadingMessage('Refreshing list...')
      }

      // Refresh the jobs list
      const refreshSuccess = await refreshJobsList()

      setTimeout(() => {
        setShowLoadingPopup(false)
      }, refreshSuccess ? 500 : 1000)

      if (matchesInvalidated) {
        await autoRematchExistingCandidates(jobId)
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update job'
      setShowLoadingPopup(false)
      toast.error(message)
    }
  })

  // Re-runs matching for every candidate already applied to a job, without
  // requiring the recruiter to open it and manually re-apply — triggered
  // automatically after editing a job from this list page changes a
  // matching-relevant field (see updateJobMutation's matchesInvalidated).
  // Already-rejected candidates are excluded server-side, not here.
  const autoRematchExistingCandidates = async (jobIdToRematch: string) => {
    try {
      const existingResponse = await matchingApi.getComparisonResults(jobIdToRematch)
      const existingResumeIds: string[] = (existingResponse.data?.comparison_results || [])
        .map((r: any) => r.resume_id)
        .filter(Boolean)

      if (existingResumeIds.length === 0) {
        return
      }

      await matchingApi.compareCandidatesAsync([jobIdToRematch], existingResumeIds)
      toast.success(`Refreshing matches for ${existingResumeIds.length} candidate(s) against the updated job...`)

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['comparison-results', jobIdToRematch] }).catch(() => {})
      }, 2000)
    } catch (error) {
      console.error('Auto re-match after job update failed:', error)
      toast.error('Job updated, but automatically refreshing candidate matches failed. Please use "Apply Selected" manually.')
    }
  }

  // Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: (jobId: string) => jobsApi.delete(jobId),
    onSuccess: () => {
      console.log('🔄 DELETE JOB SUCCESS - Refreshing jobs list...')
      toast.success('Job posting deleted successfully!')
      refreshJobsList()
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to delete job'
      toast.error(message)
    }
  })
  
  const handleOpenDeleteModal = (jobId: string) => {
    setJobIdToDelete(jobId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (jobIdToDelete) {
      deleteJobMutation.mutate(jobIdToDelete);
    }
    setIsDeleteModalOpen(false);
    setJobIdToDelete(null);
  };

  // Reprocess job mutation (admin only)
  const reprocessJobMutation = useMutation({
    mutationFn: (jobId: string) => jobsApi.reprocess(jobId),
    onSuccess: async () => {
      console.log('🔄 REPROCESS JOB SUCCESS - Starting reprocessing...')
      toast.success('Job reprocessing started!')
      
      // Show loading popup for reprocessing
      setShowLoadingPopup(true)
      setLoadingMessage('Reprocessing job with AI analysis...')
      
      // Wait a moment for backend processing
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // setLoadingMessage('Job reprocessed successfully! Refreshing list...')
      
      // Refresh the jobs list
      const refreshSuccess = await refreshJobsList()
      
      setTimeout(() => {
        setShowLoadingPopup(false)
        // toast.success('Job reprocessed successfully!')
      }, refreshSuccess ? 500 : 1000)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Job reprocessing failed'
      setShowLoadingPopup(false)
      toast.error(message)
    }
  })

  // Toggle job status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ jobId, currentStatus }: { jobId: string; currentStatus: string }) => {
      const newStatus = currentStatus === 'active' ? 'closed' : 'active'
      return jobsApi.updateStatus(jobId, newStatus)
    },
    onSuccess: (response, { currentStatus }) => {
      const newStatus = currentStatus === 'active' ? 'closed' : 'active'
      toast.success(`Job status changed to ${newStatus}`)
      refreshJobsList()
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update job status'
      toast.error(message)
    }
  })

  // JD text analysis mutation
  const analyzeJDMutation = useMutation({
    mutationFn: async (data: { jdText: string; createJob: boolean }) => {
      if (data.createJob) {
        // Close the JD analysis modal first, then show loading popup
        setShowJDAnalysisModal(false)
        setShowLoadingPopup(true)
        setLoadingMessage('Analyzing job description and creating job...')
      }
      return jobsApi.analyzeText({ 
        job_description_text: data.jdText
      })
    },
    onSuccess: async (response: any) => {
      console.log('🔍 API Response:', response.data)
      if (response.data.status === 'success') {
        if (response.data.job_created) {
          setLoadingMessage('Job created successfully! Processing response...')
          
          // Wait a moment for API interceptor to complete processing
          await new Promise(resolve => setTimeout(resolve, 200))
          
          setLoadingMessage('Job created successfully! Waiting for backend...')
          
          // Wait for backend to complete job creation
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          setLoadingMessage('Job created successfully! Refreshing list...')
          
          const refreshSuccess = await refreshJobsListAfterCreation()
          
          setTimeout(() => {
            setShowLoadingPopup(false)
            const jobId = response.data.job_id || 'Unknown'
            toast.success(`Job created successfully! ID: ${jobId}`)
          }, refreshSuccess ? 300 : 800) // Shorter delay if refresh was successful
        } else if (response.data.extracted_data) {
          console.log('📊 Extracted Data:', response.data.extracted_data)
          console.log('📊 Responsibilities:', response.data.extracted_data.responsibilities)
          console.log('📊 Requirements:', response.data.extracted_data.requirements)
          console.log('📊 Benefits:', response.data.extracted_data.benefits)
          setShowLoadingPopup(false)
          toast.success('Job description analyzed successfully!')
          setExtractedJobData(response.data.extracted_data)
          setShowJDAnalysisModal(false)
          setShowCreateModal(true)
        }
      } else {
        setShowLoadingPopup(false)
        toast.error(response.data.error_message || 'Analysis failed')
      }
    },
    onError: (error: any) => {
      setShowLoadingPopup(false)
      const message = error.response?.data?.detail || 'Failed to analyze job description'
      toast.error(message)
    }
  })

  // File upload mutation
  const uploadJDFileMutation = useMutation({
    mutationFn: async (file: File) => {
      setShowFileUploadModal(false)
      setShowLoadingPopup(true)
      setLoadingMessage('Uploading and analyzing job description...')
      
      return jobsApi.uploadJD(file)
    },
    onSuccess: async (response: any) => {
      console.log('📄 File Upload Response:', response.data)
      if (response.data.status === 'success') {
        if (response.data.job_created) {
          setLoadingMessage('Job created successfully! Processing response...')
          
          // Wait a moment for API interceptor to complete processing
          await new Promise(resolve => setTimeout(resolve, 200))
          
          setLoadingMessage('Job created successfully! Waiting for backend...')
          
          // Wait for backend to complete job creation
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          setLoadingMessage('Job created successfully! Refreshing list...')
          
          const refreshSuccess = await refreshJobsListAfterCreation()
          
          setTimeout(() => {
            setShowLoadingPopup(false)
            const jobId = response.data.job_id || 'Unknown'
            toast.success(`Job created successfully! ID: ${jobId}`)
          }, refreshSuccess ? 300 : 800)
        } else if (response.data.extracted_data) {
          console.log('📊 Extracted Data:', response.data.extracted_data)
          setShowLoadingPopup(false)
          toast.success('Job description analyzed successfully!')
          setExtractedJobData(response.data.extracted_data)
          setShowCreateModal(true)
        }
      } else {
        setShowLoadingPopup(false)
        toast.error(response.data.error_message || 'File analysis failed')
      }
    },
    onError: (error: any) => {
      console.error('❌ File Upload Error:', error)
      setShowLoadingPopup(false)
      const message = error.response?.data?.detail || error.message || 'File upload failed'
      toast.error(message)
    }
  })

  const jobs = paginatedJobs

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
            <JobInfoTooltip content="Create and manage job postings">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor transition-colors flex-shrink-0">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-600" />
              </span>
            </JobInfoTooltip>
          </div>
        </div>
        {canCreateJob && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowCreateDropdown(!showCreateDropdown)}
              disabled={showLoadingPopup}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center space-x-1.5 ${
                showLoadingPopup
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <PlusIcon className="w-4 h-4" />
              <span>Create Job</span>
              <ChevronDownIcon className="w-3.5 h-3.5" />
            </button>

            {showCreateDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      setExtractedJobData(null)
                      setShowCreateModal(true)
                      setShowCreateDropdown(false)
                    }}
                    disabled={showLoadingPopup}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Enter Details</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowJDAnalysisModal(true)
                      setShowCreateDropdown(false)
                    }}
                    disabled={showLoadingPopup}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    <span>Paste JD</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowFileUploadModal(true)
                      setShowCreateDropdown(false)
                    }}
                    disabled={showLoadingPopup}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentArrowUpIcon className="w-4 h-4" />
                    <span>Upload JD</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search & Filters - one compact row */}
      <div className="bg-white rounded-lg shadow px-4 py-2.5 space-y-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          {/* Search */}
          <div className="flex items-center gap-1.5">
          <div className="relative w-48">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by id or job title"
                className={`w-full pl-8 pr-3 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  showLoadingPopup ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={showLoadingPopup}
              />
            </div>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
              Status:
            </label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={statusFilter}
              onChange={(e) => {
                const p = new URLSearchParams(searchParams)
                if (e.target.value) { p.set('status', e.target.value) } else { p.delete('status') }
                p.set('page', '1')
                setSearchParams(p)
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Client Filter */}
          <div className="flex items-center space-x-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
              Client:
            </label>
            <div className="w-36">
              <SearchableClientDropdown
                clients={uniqueClients}
                selectedClient={clientFilter}
                onChange={(client) => {
                  const p = new URLSearchParams(searchParams)
                  if (client) { p.set('client', client) } else { p.delete('client') }
                  p.set('page', '1')
                  setSearchParams(p)
                }}
              />
            </div>
          </div>

          {/* Source Filter */}
          <div className="flex items-center space-x-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
              Source:
            </label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={sourceFilter}
              onChange={(e) => {
                const p = new URLSearchParams(searchParams)
                if (e.target.value) { p.set('source', e.target.value) } else { p.delete('source') }
                p.set('page', '1')
                setSearchParams(p)
              }}
            >
              <option value="">All Sources</option>
              {jobSources.map((source: any) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center space-x-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
              Sort by:
            </label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={sortBy}
              onChange={(e) => {
                const p = new URLSearchParams(searchParams)
                p.set('sort_by', e.target.value)
                p.set('page', '1')
                setSearchParams(p)
              }}
            >
              <option value="created_at">Created Date</option>
              <option value="title">Job Title</option>
            </select>

            <button
              onClick={() => {
                const p = new URLSearchParams(searchParams)
                p.set('sort_order', sortOrder === 'asc' ? 'desc' : 'asc')
                p.set('page', '1')
                setSearchParams(p)
              }}
              className="flex items-center space-x-1 px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
              <span>{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
            </button>
          </div>
        </div>

        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
              <FunnelIcon className="w-3.5 h-3.5 text-gray-400" />
              {statusFilter && renderFilterChip(
                'Status',
                statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1),
                () => clearFilterParam('status')
              )}
              {clientFilter && renderFilterChip('Client', clientFilter, () => clearFilterParam('client'))}
              {sourceFilter && renderFilterChip(
                'Source',
                (jobSources.find((source: any) => source.id === sourceFilter) || {}).name || sourceFilter,
                () => clearFilterParam('source')
              )}
              {isSortActive && renderFilterChip(
                'Sort',
                `${sortBy === 'title' ? 'Job Title' : 'Created Date'}, ${sortOrder === 'asc' ? 'Asc' : 'Desc'}`,
                clearSort
              )}
            </div>
            {activeFilterCount > 1 && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                aria-label="Clear filters"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Jobs List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Job Postings ({totalJobs})
          </h3>

          <div className="flex items-center space-x-3">
            {/* Export Button
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={isExporting || totalJobs === 0}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
                <span>{isExporting ? 'Exporting...' : 'Export'}</span>
              </button>
              {showExportDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => handleExportReport('xlsx')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  >
                    Export as Excel
                  </button>
                  <button
                    onClick={() => handleExportReport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                  >
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
            */}
            {/* Page Size Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-700">Show:</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-1 pr-8 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px]"
                value={pageSize}
                onChange={(e) => {
                  const p = new URLSearchParams(searchParams)
                  p.set('page_size', e.target.value)
                  p.set('page', '1')
                  setSearchParams(p)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-gray-700">per page</span>
            </div>
            
            {/* Pagination Controls */}
            {totalJobs > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const p = new URLSearchParams(searchParams)
                    p.set('page', String(Math.max(1, currentPage - 1)))
                    setSearchParams(p)
                  }}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>

                <span className="text-xs text-gray-700 px-2">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => {
                    const p = new URLSearchParams(searchParams)
                    p.set('page', String(currentPage + 1))
                    setSearchParams(p)
                  }}
                  disabled={!hasNext}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading job postings...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <ExclamationCircleIcon className="w-12 h-12 text-red-400 mx-auto" />
            <p className="mt-4 text-gray-600">Failed to load job postings</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center">
            <BriefcaseIcon className="w-12 h-12 text-gray-400 mx-auto" />
            <p className="mt-4 text-gray-600">No job postings found</p>
            <p className="text-sm text-gray-500">Create your first job posting to get started</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[65vh]">
            <table className="min-w-[850px] w-full table-fixed divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '28%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Job</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">View</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Client</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigned To</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Applications</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job: Job) => {
                  const pipelineTotal = job.pipeline_stats ? job.pipeline_stats.total : 0
                  const pipelineShortlisted = job.pipeline_stats ? job.pipeline_stats.shortlisted : 0
                  const pipelineInterview = job.pipeline_stats ? job.pipeline_stats.interview : 0
                  const pipelineSelected = job.pipeline_stats ? job.pipeline_stats.selected : 0
                  const isTitleTruncated = job.title.length > 30
                  const displayTitle = truncateJobTitle(job.title)

                  return (
                  <tr key={job.id} className="align-middle hover:bg-gray-50">
                    {/* Job */}
                    <td className="px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-black-800 rounded-full font-mono whitespace-nowrap">
                            {job.job_id || `JD: ${shortenJobId(job.id)}`}
                          </span>
                          <JobInfoTooltip
                            content={[
                              job.created_by_name ? `Created by: ${job.created_by_name}` : '',
                              `Created: ${formatToCompactDateTime(job.created_at)}`,
                              job.updated_at !== job.created_at ? `Updated: ${formatToCompactDateTime(job.updated_at)}` : '',
                            ].filter(Boolean).join(' | ')}
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor transition-colors flex-shrink-0">
                              <InformationCircleIcon className="w-4 h-4 text-gray-700 hover:text-blue-600" />
                            </span>
                          </JobInfoTooltip>
                        </div>
                        {isTitleTruncated ? (
                          <JobInfoTooltip content={job.title}>
                            <h4 className="mt-1 text-sm font-medium text-gray-900 truncate cursor-default">
                              {displayTitle}
                            </h4>
                          </JobInfoTooltip>
                        ) : (
                          <h4 className="mt-1 text-sm font-medium text-gray-900 truncate">
                            {displayTitle}
                          </h4>
                        )}
                        {job.location && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPinIcon className="w-3.5 h-3.5" />
                              {job.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* View Details */}
                    <td className="px-2 py-2 text-center">
                      {job.processing_status === 'pending' || job.processing_status === 'processing' ? (
                        <span
                          className="inline-flex p-1.5 rounded-lg text-gray-300 cursor-not-allowed"
                          title="Job is still being processed"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </span>
                      ) : (
                        <Link
                          to={`/jobs/${job.id}`}
                          className="inline-flex p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </Link>
                      )}
                    </td>

                    {/* Client */}
                    <td className="px-3 py-2">
                      {job.client ? (
                        <div className="flex items-center gap-1 text-xs text-gray-700">
                          <BuildingOfficeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="break-words">{job.client}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Assigned To */}
                    <td className="px-2 py-2 text-center">
                      {job.assigned_recruiters && job.assigned_recruiters.length > 0 ? (
                        <div className="flex justify-center">
                          <AssignedRecruitersDisplay
                            recruiters={job.assigned_recruiters}
                            showManageButton={false}
                            size="xs"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-2 py-2 text-center">
                      <div className="flex justify-center">
                        {job.processing_status === 'pending' || job.processing_status === 'processing' ? (
                          <ProcessingStatusBadge status={job.processing_status} />
                        ) : (
                          <StatusBadge status={job.status} />
                        )}
                      </div>
                    </td>

                    {/* Applications — each pill links to the job's Matching tab */}
                    <td className="px-2 py-2">
                      <div className="grid grid-cols-2 gap-1">
                        {renderPipelinePill('Submitted', pipelineTotal, job.id, 'bg-blue-50 text-blue-700')}
                        {renderPipelinePill('Shortlisted', pipelineShortlisted, job.id, 'bg-purple-50 text-purple-700')}
                        {renderPipelinePill('Interview', pipelineInterview, job.id, 'bg-orange-50 text-orange-700')}
                        {renderPipelinePill('Selected', pipelineSelected, job.id, 'bg-green-50 text-green-700')}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-2 text-center">
                      <JobRowActionsMenu
                        canDownloadJD={canDownloadJD}
                        canEditJob={canEditJob}
                        canChangeJobStatus={canChangeJobStatus}
                        canReprocessJob={canReprocessJob}
                        canDelete={can('jobs.delete')}
                        showLoadingPopup={showLoadingPopup}
                        isTogglingStatus={toggleStatusMutation.isPending}
                        isReprocessing={reprocessJobMutation.isPending}
                        isDeleting={deleteJobMutation.isPending}
                        onDownload={() => handleDownloadJob(job)}
                        onEdit={() => setEditingJob(job)}
                        onToggleStatus={() => toggleStatusMutation.mutate({ jobId: job.id, currentStatus: job.status })}
                        onReprocess={() => reprocessJobMutation.mutate(job.id)}
                        onDelete={() => handleOpenDeleteModal(job.id)}
                      />
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Bottom Pagination */}
      {totalJobs > 0 && (
        <div className="flex justify-center items-center space-x-4 bg-white p-4 rounded-lg shadow">
          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams)
              p.set('page', String(Math.max(1, currentPage - 1)))
              setSearchParams(p)
            }}
            disabled={currentPage === 1}
            className="flex items-center space-x-2 px-4 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-2">
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let startPage = Math.max(1, currentPage - 2)
              const endPage = Math.min(totalPages, startPage + 4)

              if (endPage - startPage < 4) {
                startPage = Math.max(1, endPage - 4)
              }

              const pageNum = startPage + i
              if (pageNum > totalPages) return null

              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    const p = new URLSearchParams(searchParams)
                    p.set('page', String(pageNum))
                    setSearchParams(p)
                  }}
                  className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams)
              p.set('page', String(currentPage + 1))
              setSearchParams(p)
            }}
            disabled={!hasNext}
            className="flex items-center space-x-2 px-4 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span>Next</span>
            <ChevronRightIcon className="w-4 h-4" />
          </button>

          <div className="text-xs text-gray-600 ml-4">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalJobs)} of {totalJobs} results
          </div>
        </div>
      )}

      {/* JD Analysis Modal */}
      {showJDAnalysisModal && (
        <JDAnalysisModal
          onCancel={() => setShowJDAnalysisModal(false)}
          onAnalyze={(data) => analyzeJDMutation.mutate(data)}
          isLoading={analyzeJDMutation.isPending}
        />
      )}

      {/* File Upload Modal */}
      {showFileUploadModal && (
        <FileUploadModal
          onCancel={() => {
            setShowFileUploadModal(false)
            setSelectedFile(null)
          }}
          onUpload={(file) => uploadJDFileMutation.mutate(file)}
          selectedFile={selectedFile}
          onFileSelect={setSelectedFile}
          isLoading={uploadJDFileMutation.isPending}
        />
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingJob) && (
        <JobForm
          job={editingJob}
          extractedData={extractedJobData}
          onSubmit={(data) => {
            if (editingJob) {
              // Determine if reprocessing is needed based on changed fields
              // Only reprocess if content fields changed: description, responsibilities, requirements, benefits
              const needsReprocessing = (
                data.description !== editingJob.description ||
                data.responsibilities !== editingJob.responsibilities ||
                data.requirements !== editingJob.requirements ||
                data.benefits !== editingJob.benefits
              )
              
              updateJobMutation.mutate({ id: editingJob.id, data, needsReprocessing })
            } else {
              createJobMutation.mutate(data)
            }
          }}
          onCancel={() => {
            setShowCreateModal(false)
            setEditingJob(null)
            setExtractedJobData(null)
          }}
          isLoading={createJobMutation.isPending || updateJobMutation.isPending}
          currentUser={user ? { id: user.id, fullName: user.fullName, email: user.email, role: user.role } : null}
        />
      )}

      {/* CHANGE 6: Render the ConfirmationModal at the end */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Job Posting"
      >
        Are you sure you want to delete this job? This action cannot be undone.
      </ConfirmationModal>

      {/* Assignment Manager Modal */}
      {/* Loading Popup */}
      <LoadingPopup
        isVisible={showLoadingPopup}
        message={loadingMessage}
      />

      {/* <RequirementsAgentWidget /> */}
    </div>
  )
}

// Simple job form component (modal)
const JobForm: React.FC<{
  job?: Job | null,
  extractedData?: JobFormData | null,
  onSubmit: (data: JobFormData) => void,
  onCancel: () => void,
  isLoading: boolean,
  currentUser: { id: string; fullName: string; email: string; role: string } | null
}> = ({ job, extractedData, onSubmit, onCancel, isLoading, currentUser }) => {
  
  // Use useMemo to calculate form data when extractedData or job changes
  const initialFormData = React.useMemo(() => {
    const formData = {
      title: extractedData?.title || job?.title || '',
      department: extractedData?.department || job?.department || '',
      client: extractedData?.client || job?.client || '',
      source_id: extractedData?.source_id || job?.source_id || undefined,
      location: extractedData?.location || job?.location || '',
      employment_type: extractedData?.employment_type || job?.employment_type || 'full-time',
      remote_option: extractedData?.remote_option || job?.remote_option || 'no',
      description: extractedData?.description || job?.description || '',
      responsibilities: extractedData?.responsibilities || job?.responsibilities || '',
      requirements: extractedData?.requirements || job?.requirements || '',
      benefits: extractedData?.benefits || job?.benefits || '',
      salary_min: extractedData?.salary_min || job?.salary_min,
      salary_max: extractedData?.salary_max || job?.salary_max,
      salary_currency: extractedData?.salary_currency || job?.salary_currency || 'USD',
      salary_period: extractedData?.salary_period || job?.salary_period || 'annual',
      application_deadline: extractedData?.application_deadline || job?.application_deadline,
      min_years_experience: extractedData?.min_years_experience || job?.min_years_experience || undefined,
      max_years_experience: extractedData?.max_years_experience || job?.max_years_experience || undefined,
      // Only ever comes from an Upload JD response — editing a job never
      // needs these (update_job's own field_map doesn't touch them), so no
      // need to also fall back to the `job` prop here.
      original_filename: extractedData ? extractedData.original_filename : undefined,
      content_type: extractedData ? extractedData.content_type : undefined,
      azure_blob_name: extractedData ? extractedData.azure_blob_name : undefined,
      azure_container: extractedData ? extractedData.azure_container : undefined
    }
    return formData
  }, [extractedData, job]) // Include dependencies so form updates when data changes

  const [formData, setFormData] = useState<JobFormData>(initialFormData)
  const [selectedAssignees, setSelectedAssignees] = useState<OrganizationUser[]>(() => {
    // When editing, use the job's existing assigned recruiters
    if (job?.assigned_recruiters && job.assigned_recruiters.length > 0) {
      return job.assigned_recruiters.map(r => ({
        id: r.id,
        full_name: r.name,
        email: r.email,
        role: r.role
      }))
    }
    // For new jobs, default to current user
    if (currentUser) {
      return [{ id: currentUser.id, full_name: currentUser.fullName, email: currentUser.email, role: currentUser.role }]
    }
    return []
  })
  const [assigneeError, setAssigneeError] = useState<string>('')

  // Re-sync form data only when we're actually switching to a different job
  // or new AI-extracted data arrives (initialFormData is memoized on
  // [extractedData, job], so its reference only changes for those reasons —
  // never from the user typing, including clearing a field back to empty).
  React.useEffect(() => {
    setFormData(initialFormData)
  }, [initialFormData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate client field
    if (!formData.client || formData.client.trim() === '') {
      toast.error('Client is required')
      return
    }
    
    // Validate source field
    if (!formData.source_id) {
      toast.error('Please select a source')
      return
    }
    
    // Validate assignees
    if (selectedAssignees.length === 0) {
      setAssigneeError('At least one assignee is required')
      return
    }
    
    // Validate that there are no empty items (empty strings) in fields that have content
    const hasEmptyResponsibilities = formData.responsibilities && formData.responsibilities.trim() && formData.responsibilities.split('\n').some(item => item.trim() === '')
    const hasEmptyRequirements = formData.requirements && formData.requirements.trim() && formData.requirements.split('\n').some(item => item.trim() === '')
    const hasEmptyBenefits = formData.benefits && formData.benefits.trim() && formData.benefits.split('\n').some(item => item.trim() === '')
    
    if (hasEmptyResponsibilities || hasEmptyRequirements || hasEmptyBenefits) {
      const emptySections = []
      if (hasEmptyResponsibilities) emptySections.push('Responsibilities')
      if (hasEmptyRequirements) emptySections.push('Requirements')
      if (hasEmptyBenefits) emptySections.push('Benefits')
      
      toast.error(`Please complete or remove the empty items in: ${emptySections.join(', ')}`)
      return
    }
    
    // Map selected assignees to assigned_user_ids
    const submitData: JobFormData = {
      ...formData,
      assigned_user_ids: selectedAssignees.map(u => u.id)
    }
    
    onSubmit(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Not `sticky` inside the scrollable body below — a header sticky
            within the same scroll container as the fields can end up
            painting in the same band as whatever field has scrolled to the
            top (browser/zoom-dependent), looking like the header and a
            field are overlapping. Taking the header fully out of the
            scrolling region avoids that class of bug entirely. */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {job ? 'Edit Job Posting' : 'Create New Job Posting'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.department || ''}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.client ?? ''}
                onChange={(e) => setFormData({...formData, client: e.target.value})}
                placeholder="Enter client name"
              />
            </div>
            <div>
              <SourceSelector
                type="job"
                value={formData.source_id || null}
                onChange={(sourceId) => setFormData({...formData, source_id: sourceId})}
                required={true}
                label="Source"
              />
            </div>
            <div>
              <AssigneeSelector
                selectedAssignees={selectedAssignees}
                onChange={(assignees) => {
                  setSelectedAssignees(assignees)
                  if (assignees.length > 0) {
                    setAssigneeError('')
                  }
                }}
                currentUser={currentUser ? { id: currentUser.id, full_name: currentUser.fullName, email: currentUser.email, role: currentUser.role } : { id: '', full_name: '', email: '', role: '' }}
                error={assigneeError}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.location || ''}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.employment_type}
                onChange={(e) => setFormData({...formData, employment_type: e.target.value})}
              >
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remote Option *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.remote_option}
                onChange={(e) => setFormData({...formData, remote_option: e.target.value})}
              >
                <option value="no">On Site</option>
                <option value="hybrid">Hybrid</option>
                <option value="full">Fully Remote</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
            <textarea
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe the role and responsibilities..."
            />
          </div>

          {/* Responsibilities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsibilities</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Responsibilities (click to edit):</div>
              <ul className="space-y-2">
                {formData.responsibilities !== undefined ? (
                  formData.responsibilities.split('\n').map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                    <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                    <textarea
                      value={item}
                      onChange={(e) => {
                        const lines = (formData.responsibilities || '').split('\n')
                        lines[index] = e.target.value
                        setFormData({...formData, responsibilities: lines.join('\n')})
                      }}
                      className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-blue-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                      placeholder="Enter responsibility..."
                      rows={1}
                      style={{ 
                        height: 'auto',
                        minHeight: '24px',
                        lineHeight: '1.5',
                        overflow: 'hidden',
                        wordWrap: 'break-word'
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto'
                          textarea.style.height = textarea.scrollHeight + 'px'
                        }
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                      onFocus={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                    />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.responsibilities || '').split('\n').filter((_, i) => i !== index)
                          const newResponsibilities = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newResponsibilities.trim() === '' || lines.length === 0) {
                            setFormData({...formData, responsibilities: undefined})
                          } else {
                            setFormData({...formData, responsibilities: newResponsibilities})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                  </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No responsibilities added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentResponsibilities = formData.responsibilities || ''
                  
                  // If responsibilities are empty, add one empty responsibility
                  // If responsibilities exist, add a new empty responsibility
                  let newResponsibilities
                  if (currentResponsibilities.trim().length === 0) {
                    newResponsibilities = '' // Start with empty string for first responsibility
                  } else {
                    newResponsibilities = `${currentResponsibilities}\n` // Add newline for additional responsibility
                  }
                  
                  setFormData({...formData, responsibilities: newResponsibilities})
                }}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add responsibility
              </button>
            </div>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Requirements (click to edit):</div>
              <ul className="space-y-2">
                {formData.requirements !== undefined ? (
                  formData.requirements.split('\n').map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                    <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                    <textarea
                      value={item}
                      onChange={(e) => {
                        const lines = (formData.requirements || '').split('\n')
                        lines[index] = e.target.value
                        setFormData({...formData, requirements: lines.join('\n')})
                      }}
                      className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-green-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                      placeholder="Enter requirement..."
                      rows={1}
                      style={{ 
                        height: 'auto',
                        minHeight: '24px',
                        lineHeight: '1.5',
                        overflow: 'hidden',
                        wordWrap: 'break-word'
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto'
                          textarea.style.height = textarea.scrollHeight + 'px'
                        }
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                      onFocus={(e) => {
                        const target = e.target as HTMLTextAreaElement
                        target.style.height = 'auto'
                        target.style.height = target.scrollHeight + 'px'
                      }}
                    />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.requirements || '').split('\n').filter((_, i) => i !== index)
                          const newRequirements = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newRequirements.trim() === '' || lines.length === 0) {
                            setFormData({...formData, requirements: undefined})
                          } else {
                            setFormData({...formData, requirements: newRequirements})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                  </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No requirements added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentRequirements = formData.requirements || ''
                  
                  // If requirements are empty, add one empty requirement
                  // If requirements exist, add a new empty requirement
                  let newRequirements
                  if (currentRequirements.trim().length === 0) {
                    newRequirements = '' // Start with empty string for first requirement
                  } else {
                    newRequirements = `${currentRequirements}\n` // Add newline for additional requirement
                  }
                  
                  setFormData({...formData, requirements: newRequirements})
                }}
                className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium"
              >
                + Add requirement
              </button>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[120px]">
              <div className="text-xs text-gray-600 mb-2 font-medium">Benefits (click to edit):</div>
              <ul className="space-y-2">
                {formData.benefits !== undefined ? (
                  (formData.benefits || '').split('\n').map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-700 group">
                      <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                      <textarea
                        value={item}
                        onChange={(e) => {
                          const lines = (formData.benefits || '').split('\n')
                          lines[index] = e.target.value
                          setFormData({...formData, benefits: lines.join('\n')})
                        }}
                        className="flex-1 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-purple-300 focus:px-2 focus:py-1 focus:rounded resize-none w-full leading-relaxed"
                        placeholder="Enter benefit..."
                        rows={1}
                        style={{ 
                          height: 'auto',
                          minHeight: '24px',
                          lineHeight: '1.5',
                          overflow: 'hidden',
                          wordWrap: 'break-word'
                        }}
                        ref={(textarea) => {
                          if (textarea) {
                            textarea.style.height = 'auto'
                            textarea.style.height = textarea.scrollHeight + 'px'
                          }
                        }}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement
                          target.style.height = 'auto'
                          target.style.height = target.scrollHeight + 'px'
                        }}
                        onFocus={(e) => {
                          const target = e.target as HTMLTextAreaElement
                          target.style.height = 'auto'
                          target.style.height = target.scrollHeight + 'px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const lines = (formData.benefits || '').split('\n').filter((_, i) => i !== index)
                          const newBenefits = lines.join('\n')
                          // If no lines left or only empty lines, set to undefined
                          if (newBenefits.trim() === '' || lines.length === 0) {
                            setFormData({...formData, benefits: undefined})
                          } else {
                            setFormData({...formData, benefits: newBenefits})
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-400 text-sm italic">No benefits added yet</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => {
                  const currentBenefits = formData.benefits || ''
                  
                  // If benefits are empty, add one empty benefit
                  // If benefits exist, add a new empty benefit
                  let newBenefits
                  if (currentBenefits.trim().length === 0) {
                    newBenefits = '' // Start with empty string for first benefit
                  } else {
                    newBenefits = `${currentBenefits}\n` // Add newline for additional benefit
                  }
                  
                  setFormData({...formData, benefits: newBenefits})
                }}
                className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                + Add benefit
              </button>
            </div>
          </div>

          {/* Years of Experience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
            <div className="flex space-x-2">
              <input
                type="number"
                placeholder="Min years"
                min="0"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.min_years_experience ?? ''}
                onChange={(e) => setFormData({...formData, min_years_experience: e.target.value ? parseInt(e.target.value) : undefined})}
              />
              <input
                type="number"
                placeholder="Max years"
                min="0"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.max_years_experience ?? ''}
                onChange={(e) => setFormData({...formData, max_years_experience: e.target.value ? parseInt(e.target.value) : undefined})}
              />
            </div>
          </div>

          {/* Salary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.01"
                placeholder="Min"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_min || ''}
                onChange={(e) => setFormData({...formData, salary_min: e.target.value ? parseFloat(e.target.value) : undefined})}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Max"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_max || ''}
                onChange={(e) => setFormData({...formData, salary_max: e.target.value ? parseFloat(e.target.value) : undefined})}
              />
            </div>
          </div>

          {/* Salary Period and Currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_period}
                onChange={(e) => setFormData({...formData, salary_period: e.target.value})}
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.salary_currency}
                onChange={(e) => setFormData({...formData, salary_currency: e.target.value})}
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <span>{job ? 'Update Job' : 'Create Job'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig = {
    active: { icon: CheckCircleIcon, text: 'Active', className: 'bg-green-100 text-green-800' },
    inactive: { icon: ExclamationCircleIcon, text: 'Inactive', className: 'bg-gray-100 text-gray-800' },
    closed: { icon: ExclamationCircleIcon, text: 'Closed', className: 'bg-red-100 text-red-800' }
  }

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active
  const Icon = config.icon

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.text}
    </span>
  )
}

// Processing status badge component - only show when processing
const ProcessingStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  // Only show the badge when status is 'processing' or 'pending'
  if (!status || (status !== 'processing' && status !== 'pending')) return null

  const statusConfig = {
    pending: { icon: ClockIcon, text: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
    processing: { icon: ArrowPathIcon, text: 'Processing', className: 'bg-blue-100 text-blue-800 animate-pulse' }
  }

  const config = statusConfig[status as keyof typeof statusConfig]
  if (!config) return null

  const Icon = config.icon

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {config.text}
    </span>
  )
}

// Loading popup component
const LoadingPopup: React.FC<{ 
  isVisible: boolean, 
  message: string 
}> = ({ isVisible, message }) => {
  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full text-center shadow-xl">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Processing Job</h3>
            <p className="text-gray-600">{message}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// JD Text Analysis Modal
const JDAnalysisModal: React.FC<{ 
  onCancel: () => void, 
  onAnalyze: (data: { jdText: string; createJob: boolean }) => void, 
  isLoading: boolean 
}> = ({ onCancel, onAnalyze, isLoading }) => {
  const [jdText, setJdText] = useState('')
  const [createJob, setCreateJob] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (jdText.trim().length < 50) {
      toast.error('Please provide at least 50 characters of job description text')
      return
    }
    onAnalyze({ jdText: jdText.trim(), createJob })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Analyze Job Description</h3>
            <p className="text-sm text-gray-600 mt-1">Paste your job description and let AI extract all the details</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Description Text *
              </label>
              <textarea
                required
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste your complete job description here...

Example:
Senior Software Engineer - Full Stack Development

We are looking for a Senior Software Engineer to join our growing team...

Requirements:
- 5+ years of software development experience
- Strong proficiency in React, Node.js, and Python
- Experience with cloud platforms (AWS/Azure)
- Bachelor's degree in Computer Science or related field

Responsibilities:
- Design and develop scalable web applications
- Collaborate with product and design teams
- Mentor junior developers
- Participate in code reviews

Benefits:
- Competitive salary ($120,000 - $180,000)
- Health insurance
- Remote work options
- Professional development budget"
              />
              <div className="mt-2 text-sm text-gray-500">
                {jdText.length} characters • Minimum 50 characters required
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">What AI will extract:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Job title, department, and location</li>
                <li>• Employment type and remote work options</li>
                <li>• Detailed requirements and responsibilities</li>
                <li>• Salary range and benefits (if mentioned)</li>
                <li>• Skills and experience requirements</li>
                <li>• Quality assessment and suggestions for improvement</li>
              </ul>
            </div>

            {/* Create Job Option */}
            {/* <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={createJob}
                  onChange={(e) => setCreateJob(e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <div>
                  <div className="text-sm font-medium text-green-900">
                    Create job posting immediately
                  </div>
                  <div className="text-sm text-green-700 mt-1">
                    {createJob ? (
                      <>✅ Job will be created and published automatically after analysis</>
                    ) : (
                      <>📋 Extract data for form auto-population (you can review before creating)</>
                    )}
                  </div>
                </div>
              </label>
            </div> */}
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || jdText.trim().length < 50}
              className={`px-6 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 ${
                createJob 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading && <LoadingSpinner size="sm" />}
              <span>
                {isLoading 
                  ? (createJob ? 'Creating Job...' : 'Analyzing...') 
                  : (createJob ? 'Analyze & Create Job' : 'Analyze with AI')
                }
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Helper function to format text for textarea display
const formatTextForTextarea = (text: string | undefined): string => {
  if (!text) return ''
  // Split by newlines and join with proper line breaks
  return text.split('\n').join('\n')
}

// File Upload Modal Component
const FileUploadModal: React.FC<{
  onCancel: () => void
  onUpload: (file: File) => void
  selectedFile: File | null
  onFileSelect: (file: File | null) => void
  isLoading: boolean
}> = ({ onCancel, onUpload, selectedFile, onFileSelect, isLoading }) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF, DOC, DOCX, or TXT file')
        return
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB')
        return
      }
      
      onFileSelect(file)
    }
  }

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Upload Job Description</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <DocumentArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
              >
                Click to select file
              </label>
              <p className="text-sm text-gray-500 mt-2">
                PDF, DOC, DOCX, or TXT files up to 10MB
              </p>
            </div>
          </div>
          
          {selectedFile && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium text-gray-900">{selectedFile.name}</span>
                <span className="text-xs text-gray-500">
                  ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <DocumentArrowUpIcon className="w-4 h-4" />
                <span>Upload & Analyze</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default JobsPage
